import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  ShieldCheck,
  Bitcoin,
  UserCheck,
  FileText,
  AlertTriangle,
  CheckCircle2,
  TrendingUp
} from 'lucide-react';

const FIUVDASPs = () => {
  const vdaspControls = [
    { name: 'KYC/CDD Requirements', compliance: 95, status: 'compliant', priority: 'high' },
    { name: 'Transaction Monitoring', compliance: 92, status: 'compliant', priority: 'high' },
    { name: 'STR/CTR Reporting', compliance: 98, status: 'compliant', priority: 'critical' },
    { name: 'Record Keeping', compliance: 94, status: 'compliant', priority: 'medium' },
    { name: 'Risk Assessment', compliance: 88, status: 'in-progress', priority: 'high' },
    { name: 'PEP Screening', compliance: 96, status: 'compliant', priority: 'high' }
  ];

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-green-100 text-green-700'
    };
    return colors[priority] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          FIU-IND VDASPs Compliance
        </h1>
        <p className="text-gray-600">
          Virtual Digital Asset Service Providers - AML/CFT Compliance Framework
        </p>
      </div>

      <div className="p-8 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <ShieldCheck className="w-8 h-8 text-blue-600" />
                <Badge className="bg-green-100 text-green-700">94%</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">94%</p>
              <p className="text-sm text-gray-600 mt-1">Overall Compliance</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Bitcoin className="w-8 h-8 text-purple-600" />
                <Badge variant="secondary">Live</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">23.4K</p>
              <p className="text-sm text-gray-600 mt-1">VDA Transactions/Day</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <FileText className="w-8 h-8 text-green-600" />
                <Badge className="bg-green-100 text-green-700">Filed</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">47</p>
              <p className="text-sm text-gray-600 mt-1">STRs Filed (MTD)</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <UserCheck className="w-8 h-8 text-orange-600" />
                <Badge variant="secondary">Active</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">12.8K</p>
              <p className="text-sm text-gray-600 mt-1">Verified VDA Users</p>
            </CardContent>
          </Card>
        </div>

        {/* VDASP Controls */}
        <Card>
          <CardHeader>
            <CardTitle>FIU-IND VDASP Control Framework</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {vdaspControls.map((control, index) => (
                <div key={control.name || `control-${index}`} className="p-4 rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-sm transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{control.name}</h3>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge className={getPriorityColor(control.priority)}>
                        {control.priority}
                      </Badge>
                      <Badge className={control.status === 'compliant' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                        {control.status}
                      </Badge>
                      <span className="text-lg font-bold text-gray-900">{control.compliance}%</span>
                    </div>
                  </div>
                  <Progress value={control.compliance} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* STR/CTR Reporting */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Suspicious Transaction Reports (STR)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-600">STRs Filed This Month</p>
                      <p className="text-3xl font-bold text-gray-900">47</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Badge className="bg-green-100 text-green-700">+12%</Badge>
                    <span className="text-gray-600">vs last month</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Under Review</span>
                    <span className="font-semibold text-gray-900">12</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Submitted to FIU</span>
                    <span className="font-semibold text-gray-900">35</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Avg. Filing Time</span>
                    <span className="font-semibold text-gray-900">2.3 days</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>VDA-Specific Risk Indicators</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Mixer/Tumbler Usage</p>
                      <p className="text-xs text-gray-600">Privacy coin transactions</p>
                    </div>
                    <Badge className="bg-red-100 text-red-700">23 alerts</Badge>
                  </div>
                </div>
                <div className="p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Rapid Exchange Activity</p>
                      <p className="text-xs text-gray-600">High velocity trading</p>
                    </div>
                    <Badge className="bg-yellow-100 text-yellow-700">45 alerts</Badge>
                  </div>
                </div>
                <div className="p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Cross-Border VDA Transfers</p>
                      <p className="text-xs text-gray-600">High-risk jurisdictions</p>
                    </div>
                    <Badge className="bg-orange-100 text-orange-700">67 alerts</Badge>
                  </div>
                </div>
                <div className="p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">P2P Trading Patterns</p>
                      <p className="text-xs text-gray-600">Unregistered exchange activity</p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700">34 alerts</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FIUVDASPs;
